import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigurationsService } from 'src/app/services/api/configurations.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { DatePipe } from '@angular/common';
import { ColumnFilterComponent } from '../../instances-mgmt/column-filter/column-filter.component';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import moment from 'moment';
import { ActivityService } from 'src/app/services/api/activity.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-environment-metrics',
  templateUrl: './environment-metrics.component.html',
  styleUrl: './environment-metrics.component.scss',
  standalone: false,
})
export class EnvironmentMetricsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  @Input() instanceData;
  @Input() instanceId;
  @Input() configurations;
  @Input() runHours: any;
  @Input() shutDownHours: any;
  @Input() timetakenChartLoaded = false;
  @Input() isResponseOk = false;
  @Input() isErrorResponse = false;
  @Input() instance;

  metricsDisplayColumns = [
    {
      key: 'operation',
      name: translate('INSTANCE_DETAILS.ACTION'),
      type: 'text',
      sortable: true,
      visible: true,
    },
    {
      key: 'startTime',
      name: translate('INSTANCE_DETAILS.START_TIME'),
      type: 'date',
      sortable: true,
      visible: true,
    },
    {
      key: 'finishTime',
      name: translate('INSTANCE_DETAILS.END_TIME'),
      type: 'date',
      sortable: true,
      visible: true,
    },
    {
      key: 'totalDuration',
      name: translate('INSTANCE_DETAILS.TOTAL_DURATION'),
      type: 'duration',
      sortable: true,
      visible: true,
    },
  ];
  metricsLog: any;
  metricsSearchTerm: any;
  successConfigStates = [];
  metricsPieChartLoaded: boolean;
  metricsPiechart: any;
  metricMonth = new Date();
  configurationStateResponse: any;
  configurationMonth: any;
  selectedMetricInstance: any;
  metricTimePieMonth: any;
  metricFilter = [];
  metricActionOptions = [];
  metricAction: any;
  selectedMetricAction: any;
  metricLogData = [];
  metricStartDate: any;
  metricEndDate: any;
  searchedMetricResult = [];
  filteredColumns: any;
  showActivityLoader = true;
  isLogFailed: boolean;
  metricResponse: any;

  constructor(
    private instanceService: InstancesService,
    private configService: ConfigurationsService,
    private toastrService: CustomToastrService,
    public datePipe: DatePipe,
    private sidenavService: SidenavService,
    private activityService: ActivityService
  ) {}

  ngOnInit(): void {
    this.metricFilter = [
      { title: 'Action', placeholder: 'Select Action' },
      {
        title: 'Date',
        placeholder: 'Select Date',
        options: { value: 'Date', selected: false },
      },
    ];
    this.metricStartDate = localStorage.getItem('filteredMetricStartDate')
      ? localStorage.getItem('filteredMetricStartDate')
      : '';
    this.metricEndDate = localStorage.getItem('filteredMetricEndDate')
      ? localStorage.getItem('filteredMetricEndDate')
      : '';
    this.getInstanceActivityLogs();
    this.configService.getConfigurations(this.instance?.blueprintId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((configurations) => {
        this.configurations = configurations;
        this.getMetricsPieChartData();
      },
      (error) => {
        setTimeout(() => {
          const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : error?.error?.message ? error?.error?.message : error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR');
          this.toastrService.errMessage(message, title);
        }, 500);
      }
    );
    if (this.instance) {
      const now = new Date();
      const currentYearMonth = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, '0')}`;
      this.loadUsageSummary(this.instanceId, currentYearMonth);
      this.metricTimePieMonth = currentYearMonth;
      this.selectedMetricInstance = this.instanceId;
    }
  }

  // fetching instance activity logs with pagination handling
  getInstanceActivityLogs(): void {
    this.activityService.getInstanceActivityLogs(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        const total = response?.total ?? 0;
        const pageSize = response?.size ?? 10;
        const totalPages = response?.pages ?? 1;
        const firstPageData = response?.data ?? response ?? [];

        if (totalPages <= 1) {
          this.processMetricsLogs(firstPageData);
        } else {
          const pageRequests = Array.from({ length: totalPages - 1 }, (_, i) =>
            this.activityService.getInstanceActivityLogs(this.instanceId, i + 1, pageSize)
          );

          forkJoin(pageRequests).pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(responses => {
              const allData = [
                ...firstPageData,
                ...responses.flatMap(r => r?.data ?? [])
              ];
              this.processMetricsLogs(allData);
            },
            error => this.handleActivityLogError(error)
          );
        }
      },
      error => this.handleActivityLogError(error)
    );
  }

  private processMetricsLogs(allData: any[]): void {
    this.isLogFailed = true;
    this.metricResponse = allData;

    // this.metricActionOptions = [];
    this.metricResponse?.forEach((item) => {
      if (!this.metricActionOptions.find((option) => option.value === item.operation) && item.operation !== '' && item.operation !== undefined) {
        const actionOption = {
          value: item.operation,
          selected: false,
          label: item.operation,
        };
        this.metricActionOptions = [...this.metricActionOptions, actionOption];
      }
      this.metricFilter[0].options = this.metricActionOptions;
    });

    this.filterByMonth(this.metricMonth);

    this.metricLogData = this.metricsLog;
    this.selectedMetricAction = [];
    this.metricStartDate = undefined;
    this.metricEndDate = undefined;
    this.metricFilter[0]?.options?.forEach((item) => {
      item.selected = false;
    });
    localStorage.removeItem('filteredMetricAction');
    localStorage.removeItem('filteredMetricStartDate');
    localStorage.removeItem('filteredMetricEndDate');
  }

  private filterByMonth(month: Date): void {
    // this.metricMonth = new Date(month);
    this.metricLogData = this.metricResponse.filter((item) => {
      const itemDate = new Date(item.startTime);
      return (
        itemDate.getFullYear() === this.metricMonth.getFullYear() &&
        itemDate.getMonth() === this.metricMonth.getMonth()
      );
    });
    this.metricsLog = this.metricLogData;
    this.showActivityLoader = false;
  }

  private handleActivityLogError(error: any): void {
    this.isLogFailed = true;
    this.showActivityLoader = false;
    setTimeout(() => {
      const title = translate('ERRORS.SERVICE_AVAILABILITY');
      const message = error?.message ? error?.message : error?.error?.message ? error?.error?.message : error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR');
      this.toastrService.errMessage(message, title);
    }, 500);
  }

  initialMetricItems() {
    this.metricLogData = this.metricsLog;
    if (this.selectedMetricAction?.length > 0) {
      this.metricLogData = this.metricLogData?.filter((item) =>
        this.selectedMetricAction?.includes(item?.operation)
      );
    }
    if (this.metricStartDate && this.metricEndDate) {
      this.metricLogData = this.metricLogData.filter(
        (item) =>
          this.datePipe.transform(item.startTime, 'yyyy-MM-dd') >=
            this.datePipe.transform(this.metricStartDate, 'yyyy-MM-dd') &&
          this.datePipe.transform(item.finishTime, 'yyyy-MM-dd') <=
            this.datePipe.transform(this.metricEndDate, 'yyyy-MM-dd')
      );
    }
  }

  // getting configurations states and success states count for showing in metrics pie chart
  getMetricsPieChartData(): void {
    if (this.instance?.configurationId) {
      this.configService
        .getConfigurationStates(this.instanceId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((configurationStates) => {
          this.configurationStateResponse = configurationStates;
          this.modifyConfigState();
        });
    } else {
      this.loadMetricsPieChart();
    }
  }

  modifyConfigState(): void {
    let successStates = [];
    const configurationStates = this.configurationStateResponse?.filter(
      (item) => {
        const itemDate = new Date(item?.applicationStartTime);
        return (
          itemDate.getFullYear() === this.metricMonth.getFullYear() &&
          itemDate.getMonth() === this.metricMonth.getMonth()
        );
      }
    );
    configurationStates?.forEach((item) => {
      if (
        item.status === 'success' &&
        !successStates.find((element) => element === item.configurationId)
      ) {
        successStates = [...successStates, item.configurationId];
      }
    });
    this.successConfigStates = successStates;
    this.loadMetricsPieChart();
  }

  // forming metrics pie chart parameters
  loadMetricsPieChart(): void {
    this.configurationMonth = this.configurations?.filter((item) => {
      const itemDate = new Date(item?.dateCreated);
      return (
        itemDate.getFullYear() === this.metricMonth.getFullYear() &&
        itemDate.getMonth() === this.metricMonth.getMonth()
      );
    });
    const data = [
      {
        value:
          this.configurationMonth?.length - this.successConfigStates?.length,
        name: 'Not Deployed',
      },
      { value: this.successConfigStates?.length, name: 'Deployed' },
    ];
    const colorCodes = ['#B3DADE', '#008392'];
    this.metricsPiechart = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}',
      },
      series: [
        {
          type: 'pie',
          radius: ['80%', '100%'],
          avoidLabelOverlap: false,
          top: 20,
          bottom: 10,
          right: 10,
          left: 10,
          label: {
            show: false,
            position: 'bottom',
            formatter: '',
            fontSize: '20',
          },
          labelLine: {
            show: false,
          },
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 0,
          },
          color: colorCodes,
          data: data,
        },
      ],
    };
    this.metricsPieChartLoaded = true;
  }
  /**
   * filtering metric table based on selected month
   * @param selectedMonth
   */
  applyMonthFilter(selectedMonth) {
    this.metricMonth = new Date(selectedMonth);
    this.metricLogData = this.metricResponse?.filter((item) => {
      const itemDate = new Date(item.startTime);
      return (
        itemDate.getFullYear() === this.metricMonth.getFullYear() &&
        itemDate.getMonth() === this.metricMonth.getMonth()
      );
    });
    this.metricsLog = this.metricLogData;
    this.modifyConfigState();
    const year = this.metricMonth.getFullYear();
    const month = (this.metricMonth.getMonth() + 1).toString().padStart(2, '0');
    const yyyyMm = `${year}-${month}`;
    this.metricTimePieMonth = yyyyMm;
    this.loadUsageSummary(this.selectedMetricInstance, this.metricTimePieMonth);
  }

  loadUsageSummary(instanceId, month) {
    this.timetakenChartLoaded = false;
    this.isErrorResponse = false;
    this.isResponseOk = false;
    this.instanceService
      .getInstanceUsageSummarybyId(instanceId, month)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data) => {
          const usageSum =
            Array.isArray(data) && data.length > 0 ? data[0] : null;
          if (usageSum) {
            if (usageSum.runningHours) this.runHours = usageSum.runningHours;
            if (usageSum.shutdownHours)
              this.shutDownHours = usageSum.shutdownHours;
          }
          this.isResponseOk = true;
        },
        (error) => {
          this.isErrorResponse = true;
          setTimeout(() => {
            const title = translate('ERRORS.SERVICE_AVAILABILITY');
            const message = error?.message
              ? error?.message
              : error?.error?.message
              ? error?.error?.message
              : error?.errmsg
              ? error?.errmsg
              : translate('SERVICE.INTERNAL_SERVER_ERROR');
            this.toastrService.errMessage(message, title);
          }, 500);
        }
      );
  }
  // search term from metrics tab activity
  searchMetrcis(event) {
    this.metricsSearchTerm = event;
  }
  searchedMetricItems(result) {
    this.searchedMetricResult = result;
  }
  // displying filter popup when user click on filter button
  filterMetricLogs() {
    if (this.metricAction) {
      this.metricFilter[0].options.filter((item) => {
        if (
          this.selectedMetricAction?.find((action) => action === item.value)
        ) {
          item.selected = true;
        }
      });
    }
    // updating min, max date of table filter based on month filter
    const today = new Date();
    const dateCreated = new Date(this.instance?.dateCreated);
    const minDate =
      dateCreated.getFullYear() === this.metricMonth.getFullYear() &&
      dateCreated.getMonth() === this.metricMonth.getMonth()
        ? this.instance?.dateCreated
        : this.metricMonth;
    const maxDate =
      this.metricMonth.getFullYear() === today.getFullYear() &&
      this.metricMonth.getMonth() === today.getMonth()
        ? today
        : new Date(
            this.metricMonth.getFullYear(),
            this.metricMonth.getMonth() + 1,
            0
          );
    const data: any = {
      name: translate('TENANT_DETAILS.METRICS'),
      title: translate('INSTANCE_STATUS.FILTER_TITLE'),
      subtitle: translate('INSTANCE_STATUS.FILTER_SUBTITLE'),
      isMultiSelect: true,
      filteredColumns: this.metricFilter,
      metricAction: this.metricFilter[0].options,
      metricStartDate: this.metricStartDate ? moment(this.metricStartDate) : '',
      metricEndDate: this.metricEndDate ? moment(this.metricEndDate) : '',
      metricColumns: ['action', 'date'],
      metricMinDate: minDate ? moment(minDate) : '',
      metricMaxDate: maxDate ? moment(maxDate) : '',
    };
    this.sidenavService
      .openInRightSidenav(ColumnFilterComponent, data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filteredColumns) => {
        if (filteredColumns) {
          if (filteredColumns[0] !== '') {
            this.metricAction = filteredColumns[0];
          }
          if (
            filteredColumns[0]?.length === 0 &&
            filteredColumns[1] &&
            !filteredColumns[1].filteredStartDate &&
            !filteredColumns[1].filteredEndDate
          ) {
            this.resetMetricFilter();
          } else {
            this.filterMetricItems(filteredColumns);
          }
        }
      });
  }
  /**
   * filtering metric table based on filteredColumns
   * @param filteredColumns
   */
  filterMetricItems(filteredColumns): void {
    this.metricLogData = this.metricsLog;
    if (!filteredColumns) {
      this.initialMetricItems();
    }
    if (filteredColumns) {
      this.selectedMetricAction = [];
      this.metricStartDate = undefined;
      this.metricEndDate = undefined;
      this.metricFilter[0].options.filter((item) => {
        item.selected = false;
      });
      if (filteredColumns[0]?.length > 0) {
        this.selectedMetricAction = filteredColumns[0];
        localStorage.setItem(
          'filteredMetricAction',
          JSON.stringify(this.selectedMetricAction)
        );
        this.metricLogData = this.metricLogData?.filter((item) =>
          this.selectedMetricAction?.includes(item?.operation)
        );
      } else {
        localStorage.removeItem('filteredMetricAction');
      }
      if (
        filteredColumns[1] &&
        filteredColumns[1].filteredStartDate &&
        filteredColumns[1].filteredEndDate
      ) {
        this.metricStartDate = filteredColumns[1].filteredStartDate;
        this.metricEndDate = filteredColumns[1].filteredEndDate;
        localStorage.setItem('filteredMetricStartDate', this.metricStartDate);
        localStorage.setItem('filteredMetricEndDate', this.metricEndDate);
        this.metricLogData = this.metricLogData.filter(
          (item) =>
            this.datePipe.transform(item.startTime, 'yyyy-MM-dd') >=
              this.datePipe.transform(this.metricStartDate, 'yyyy-MM-dd') &&
            this.datePipe.transform(item.finishTime, 'yyyy-MM-dd') <=
              this.datePipe.transform(this.metricEndDate, 'yyyy-MM-dd')
        );
      } else {
        localStorage.removeItem('filteredMetricStartDate');
        localStorage.removeItem('filteredMetricEndDate');
      }
    }
  }
  getAction(action) {
    return this.metricActionOptions.find((item) => item.value === action).label;
  }
  /**
   * removed selected action filter
   * @param data
   */
  cancelMetricAction(data) {
    let selectedItem = this.selectedMetricAction.indexOf(data);
    if (selectedItem > -1) {
      localStorage.removeItem('filteredMetricAction');
      this.selectedMetricAction.splice(selectedItem, 1);
      this.metricFilter[0].options.find(
        (item) => item.value === data
      ).selected = false;
      localStorage.setItem(
        'filteredMetricAction',
        JSON.stringify(this.selectedMetricAction)
      );
      this.filterMetricItems(this.filteredColumns);
    }
  }
  // remevod selected date filter
  cancelMetricDate() {
    localStorage.removeItem('filteredMetricStartDate');
    localStorage.removeItem('filteredMetricEndDate');
    this.metricEndDate = undefined;
    this.metricStartDate = undefined;
    this.filterMetricItems(this.filteredColumns);
  }
  // resetting metric dasboard filter
  resetMetricFilter() {
    this.selectedMetricAction = [];
    this.metricEndDate = undefined;
    this.metricStartDate = undefined;
    this.metricFilter[0].options.filter((item) => {
      item.selected = false;
    });
    this.metricLogData = this.metricsLog;
    localStorage.removeItem('filteredMetricAction');
    localStorage.removeItem('filteredMetricStartDate');
    localStorage.removeItem('filteredMetricEndDate');
  }
}
